import { Component } from '@theme/component';

/**
 * Anchor navigation.
 *
 * A horizontal bar of links that jump to in-page anchors. Links whose `href`
 * contains a `#fragment` matching an element on the page are smooth-scrolled to
 * (offset by the sticky bar + header height); a scroll spy keeps the matching
 * link marked as current. Links pointing elsewhere behave like normal links.
 *
 * @typedef {Object} Refs
 * @property {HTMLAnchorElement[]} [links]
 *
 * @extends {Component<Refs>}
 */
class AnchorNavComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    this.#setupScrollSpy();

    const initial = this.#linkForHash(window.location.hash) ?? this.links[0];
    if (initial) this.#setActiveLink(initial);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /** @returns {HTMLAnchorElement[]} */
  get links() {
    return this.refs.links ?? [];
  }

  /**
   * In-page targets for links that reference an existing element.
   *
   * @returns {{ link: HTMLAnchorElement, target: HTMLElement }[]}
   */
  get #targets() {
    return this.links
      .map((link) => {
        const target = this.#targetFor(link);
        return target ? { link, target } : null;
      })
      .filter((entry) => entry != null);
  }

  /**
   * Resolves the in-page element a link points to, if any.
   *
   * @param {HTMLAnchorElement} link
   * @returns {HTMLElement | null}
   */
  #targetFor(link) {
    const href = link.getAttribute('href') ?? '';
    const index = href.indexOf('#');
    if (index === -1) return null;

    const id = href.slice(index + 1);
    if (!id) return null;

    return document.getElementById(decodeURIComponent(id));
  }

  /**
   * @param {string} hash
   * @returns {HTMLAnchorElement | undefined}
   */
  #linkForHash(hash) {
    if (!hash || hash === '#') return undefined;
    return this.links.find((link) => (link.getAttribute('href') ?? '').endsWith(hash));
  }

  /**
   * The amount of space occupied by the sticky bar and header, so scroll
   * targets land below them rather than underneath.
   *
   * @returns {number}
   */
  get #scrollOffset() {
    const barHeight = this.hasAttribute('sticky') ? this.offsetHeight : 0;
    const headerHeight =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    return barHeight + headerHeight;
  }

  /**
   * Smooth-scrolls to the clicked link's in-page target. Falls through to the
   * browser's default behavior for links that don't reference the page.
   *
   * @param {Event} event
   */
  navigate(event) {
    const link = /** @type {HTMLElement} */ (event.target).closest('a');
    if (!(link instanceof HTMLAnchorElement) || !this.links.includes(link)) return;

    const target = this.#targetFor(link);
    if (!target) return;

    event.preventDefault();

    const top = target.getBoundingClientRect().top + window.scrollY - this.#scrollOffset;
    window.scrollTo({ top, behavior: 'smooth' });

    const id = target.id;
    if (id) window.history.replaceState(null, '', `#${id}`);

    this.#setActiveLink(link);
  }

  #setupScrollSpy() {
    const targets = this.#targets;
    if (targets.length === 0) return;

    const offset = Math.round(this.#scrollOffset);
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const match = targets.find((item) => item.target === entry.target);
          if (match) this.#setActiveLink(match.link);
        }
      },
      {
        // Activate a section once it crosses just below the sticky bar, and
        // keep it active until the next one reaches that line.
        rootMargin: `-${offset}px 0px -60% 0px`,
        threshold: 0,
      }
    );

    for (const { target } of targets) this.#observer.observe(target);
  }

  /**
   * @param {HTMLAnchorElement} activeLink
   */
  #setActiveLink(activeLink) {
    for (const link of this.links) {
      const isActive = link === activeLink;
      link.classList.toggle('anchor-nav__link--active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('anchor-nav-component')) {
  customElements.define('anchor-nav-component', AnchorNavComponent);
}
